import { Link, useRouterState } from "@tanstack/react-router";
import { Home, Heart, HeartHandshake, Building2, Menu, X, Sparkles, Gift, BookOpen } from "lucide-react";
import { useState } from "react";
import { daanAmrutLogo } from "@/project/data/sample";

const logoUrl = daanAmrutLogo;


export function MobileBottomNav() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const [drawerOpen, setDrawerOpen] = useState(false);

  const tabs = [
    { to: "/", label: "Home", icon: Home },
    { to: "/initiatives", label: "Initiatives", icon: HeartHandshake },
    { to: "/donate", label: "Donate", icon: Heart, isPrimary: true },
    { to: "/causes", label: "Causes", icon: Building2 },
  ];

  return (
    <>
      {/* Mobile Bottom Navigation Bar - Sticky to bottom on small devices */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 z-50 border-t border-border/80 bg-background/95 backdrop-blur-lg px-2 py-1.5 shadow-[0_-4px_20px_rgba(0,0,0,0.08)]">
        <div className="flex items-center justify-around max-w-md mx-auto">
          {tabs.map((tab) => {
            const isActive = tab.to === "/" ? pathname === "/" : pathname.startsWith(tab.to);
            const Icon = tab.icon;

            if (tab.isPrimary) {
              return (
                <Link
                  key={tab.to}
                  to={tab.to}
                  className="relative -top-3 flex flex-col items-center group focus:outline-none"
                >
                  <div className="grid h-12 w-12 place-items-center rounded-full bg-gradient-to-tr from-[var(--maroon-deep)] to-[var(--maroon)] text-white shadow-lg shadow-[var(--maroon)]/30 ring-4 ring-background transition-transform active:scale-95 group-hover:scale-105">
                    <Icon className="h-5 w-5 fill-current" />
                  </div>
                  <span className="text-[10px] font-bold text-[var(--maroon)] mt-0.5 tracking-tight">
                    {tab.label}
                  </span>
                </Link>
              );
            }

            return (
              <Link
                key={tab.to}
                to={tab.to}
                className={`flex flex-col items-center py-1 px-3 rounded-xl transition-all ${
                  isActive
                    ? "text-[var(--maroon)] font-bold scale-105"
                    : "text-muted-foreground hover:text-foreground active:scale-95"
                }`}
              >
                <Icon className={`h-5 w-5 ${isActive ? "stroke-[2.5]" : "stroke-2"}`} />
                <span className="text-[10px] mt-0.5 font-medium">{tab.label}</span>
              </Link>
            );
          })}

          <button
            onClick={() => setDrawerOpen(true)}
            className="flex flex-col items-center py-1 px-3 rounded-xl text-muted-foreground hover:text-foreground active:scale-95 transition-all"
            aria-label="Open Navigation Drawer"
          >
            <Menu className="h-5 w-5 stroke-2" />
            <span className="text-[10px] mt-0.5 font-medium">Menu</span>
          </button>
        </div>
      </div>

      {/* Mobile Drawer Sheet Overlay */}
      {drawerOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex flex-col bg-background/95 backdrop-blur-xl animate-in fade-in duration-200">
          {/* Drawer Header */}
          <div className="flex items-center justify-between border-b border-border/80 px-5 py-4">
            <div className="flex items-center gap-3">
              <img src={logoUrl} alt="Daan Amrut" className="h-9 w-auto object-contain" />
              <div>
                <h3 className="font-display text-sm font-extrabold text-[var(--maroon-deep)]">Daan Amrut</h3>
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Seva · Transparency · Impact</p>
              </div>
            </div>
            <button
              onClick={() => setDrawerOpen(false)}
              className="grid h-9 w-9 place-items-center rounded-full border border-border bg-muted/50 hover:bg-muted text-foreground"
              aria-label="Close Drawer"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Quick Portal Switch Banner */}
          <div className="bg-gradient-to-r from-[var(--maroon-deep)] to-[var(--maroon)] px-5 py-3 text-white">
            <p className="text-xs font-semibold">Access Console Portals</p>
            <div className="mt-2 grid grid-cols-3 gap-2">
              <Link
                to="/donor"
                onClick={() => setDrawerOpen(false)}
                className="rounded-lg bg-white/15 hover:bg-white/25 py-2 px-1.5 text-center text-[11px] font-semibold text-white transition"
              >
                Donor
              </Link>
              <Link
                to="/institution"
                onClick={() => setDrawerOpen(false)}
                className="rounded-lg bg-white/15 hover:bg-white/25 py-2 px-1.5 text-center text-[11px] font-semibold text-white transition"
              >
                Gaushala
              </Link>
              <Link
                to="/admin"
                onClick={() => setDrawerOpen(false)}
                className="rounded-lg bg-[var(--saffron)] text-[var(--maroon-deep)] hover:bg-white py-2 px-1.5 text-center text-[11px] font-bold transition"
              >
                Admin
              </Link>
            </div>
          </div>

          {/* Drawer Menu Items */}
          <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
            <div>
              <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-2">Explore Modules</p>
              <div className="grid grid-cols-2 gap-2">
                <Link
                  to="/initiatives"
                  onClick={() => setDrawerOpen(false)}
                  className="flex items-center gap-2.5 rounded-xl border border-border/80 p-3 text-xs font-medium hover:bg-muted/50 transition"
                >
                  <Sparkles className="h-4 w-4 text-[var(--saffron-deep)]" /> Initiatives
                </Link>
                <Link
                  to="/organisations"
                  onClick={() => setDrawerOpen(false)}
                  className="flex items-center gap-2.5 rounded-xl border border-border/80 p-3 text-xs font-medium hover:bg-muted/50 transition"
                >
                  <Building2 className="h-4 w-4 text-[var(--maroon)]" /> Organisations
                </Link>
                <Link
                  to="/campaigns"
                  onClick={() => setDrawerOpen(false)}
                  className="flex items-center gap-2.5 rounded-xl border border-border/80 p-3 text-xs font-medium hover:bg-muted/50 transition"
                >
                  <Gift className="h-4 w-4 text-[var(--maroon)]" /> Campaigns
                </Link>
                <Link
                  to="/events"
                  onClick={() => setDrawerOpen(false)}
                  className="flex items-center gap-2.5 rounded-xl border border-border/80 p-3 text-xs font-medium hover:bg-muted/50 transition"
                >
                  <BookOpen className="h-4 w-4 text-[var(--saffron-deep)]" /> Events
                </Link>
              </div>
            </div>

            <div>
              <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-2">Foundation Pages</p>
              <div className="space-y-1">
                {[
                  { to: "/about", label: "About Us" },
                  { to: "/leadership", label: "Leadership & Advisory Board" },
                  { to: "/stories", label: "Rescue & Impact Stories" },
                  { to: "/faq", label: "80G Tax Exemption & FAQ" },
                  { to: "/contact", label: "Helpline & Emergency Contact" },
                  { to: "/partner-apply", label: "Apply as Partner NGO" },
                  { to: "/register-institution", label: "Register Gaushala / Panjrapole" },
                ].map((item) => (
                  <Link
                    key={item.to}
                    to={item.to}
                    onClick={() => setDrawerOpen(false)}
                    className="flex items-center justify-between rounded-lg px-3 py-2.5 text-xs font-medium text-foreground/80 hover:bg-muted/60 transition"
                  >
                    <span>{item.label}</span>
                    <span className="text-muted-foreground text-[10px]">→</span>
                  </Link>
                ))}
              </div>
            </div>
          </div>

          {/* Drawer Footer CTA */}
          <div className="border-t border-border/80 p-4 bg-muted/30 pb-8">
            <Link
              to="/donate"
              onClick={() => setDrawerOpen(false)}
              className="flex w-full items-center justify-center gap-2 rounded-full bg-[var(--maroon)] py-3 text-sm font-bold text-white shadow-md hover:bg-[var(--maroon-deep)]"
            >
              <Heart className="h-4 w-4 fill-white" /> Quick Donate with 80G Receipt
            </Link>
          </div>
        </div>
      )}
    </>
  );
}
